import classNames from 'classnames';
import React, { use, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { REDESIGN_BREADCRUMBS_HEIGHT_CSS_VARIABLE } from 'src/components/common/breadcrumbs/breadcrumbs-helper';
import RedesignLinkDropdownCta from 'src/components/common/redesign-link-dropdown-cta';
import { WebsiteLink } from 'src/components/common/website-link';
import MenuIcon from 'src/components/icons/menu-icon';
import RedesignClose from 'src/components/icons/redesign-close';
import RedesignLeftChevronThin from 'src/components/icons/redesign-left-chevron-thin';
import type { HeaderButtonProps } from 'src/components/layout/redesign/header-mobile/header-button';
import HeaderButton from 'src/components/layout/redesign/header-mobile/header-button';
import HeaderMenu from 'src/components/layout/redesign/header-mobile/header-menu';
import type { BaseHeaderContent } from 'src/components/layout/redesign/header-mobile/header-menu';
import { HeaderLogo } from 'src/components/layout/redesign/header-shared/header-logo';
import {
  HEADER_ADDITIONAL_TOP_PADDING_PX,
  REDESIGN_HEADER_BAR_HEIGHT_CSS_VARIABLE,
  REDESIGN_HEADER_HEIGHT_CSS_VARIABLE,
} from 'src/components/layout/redesign/header-shared/header-styling';
import { getClubInformation } from 'src/content/club-look-ups-by-id';
import { SiteContext } from 'src/context/site-context';
import useWindowSize from 'src/custom-hooks/use-window-size';
import {
  amplitudeBrandName,
  amplitudeLogoClickEvent,
  amplitudeTrackEvent,
} from 'src/helpers/amplitude-event-track';
import { clubNameDependingOnBuildTypeAndLanguage } from 'src/helpers/club-helper';
import { STACKED_STICKY_ITEM_HEIGHT_REDUCTION } from 'src/styling/constants';
import { matchReducedMotion } from 'src/styling/media-queries';

import throttleToAnimationFrames from '../../../../helpers/throttle-to-animation-frames';

import * as styles from './index.module.scss';

const COOKIE_CONTAINER_ID = 'ccc'; // Civic's cookie banner

type HeaderMobileProps = {
  headerContent: BaseHeaderContent | null;
  ribbonChildren?: React.ReactNode;
  isMenuOpen: boolean;
  setMenuOpen: (isOpen: boolean) => void;
  primaryButton?: HeaderButtonProps;
  secondaryButton?: HeaderButtonProps;
  onMenuChanged?: () => void;
  isAdditionalSubMenuOpen?: boolean;
  additionalSubMenus?: React.ReactNode;
  forceMobileHeader?: boolean;
};

const HeaderMobile = ({
  headerContent,
  ribbonChildren,
  isMenuOpen,
  setMenuOpen,
  primaryButton,
  secondaryButton,
  isAdditionalSubMenuOpen,
  additionalSubMenus,
  forceMobileHeader = false,
  ...props
}: HeaderMobileProps) => {
  const { styling, pageLevel, siteId, isGlobalPage, buildType, language, isHarbour } =
    use(SiteContext);
  const { isMobileHeader } = useWindowSize();

  const topSectionRef = useRef<HTMLDivElement>(null);
  const headerBarRef = useRef<HTMLDivElement>(null);

  const [selectedLevel1Index, setSelectedLevel1Index] = useState<number | null>(0);
  const [selectedLevel2Index, setSelectedLevel2Index] = useState<number | null>(null);

  const setHeaderBarHeightCssVariablePx = (heightPx: number) => {
    const nextValue = `${heightPx}px`;
    const docStyle = document.documentElement.style;
    if (docStyle.getPropertyValue(`--${REDESIGN_HEADER_BAR_HEIGHT_CSS_VARIABLE}`) === nextValue) {
      return;
    }
    docStyle.setProperty(`--${REDESIGN_HEADER_BAR_HEIGHT_CSS_VARIABLE}`, nextValue);
  };

  const controlCookieContainerVisibility = (displayValue: 'none' | 'block') => {
    const container = document.querySelector<HTMLElement>(`#${COOKIE_CONTAINER_ID}`);

    if (!container) return;

    container.style.display = displayValue;
  };

  useLayoutEffect(() => {
    const topSectionElement = topSectionRef.current;
    if (!topSectionElement) {
      return;
    }

    document.documentElement.style.setProperty(
      'scroll-padding-top',
      `calc(var(--${REDESIGN_HEADER_HEIGHT_CSS_VARIABLE}) + var(--${REDESIGN_BREADCRUMBS_HEIGHT_CSS_VARIABLE}, 0px))`
    );

    const updateHeaderHeights = () => {
      if (headerBarRef.current) {
        setHeaderBarHeightCssVariablePx(headerBarRef.current.offsetHeight);
      }

      const containerHeight = topSectionElement.offsetHeight;
      if (!containerHeight) {
        return;
      }

      const nextValue = `${containerHeight - HEADER_ADDITIONAL_TOP_PADDING_PX - STACKED_STICKY_ITEM_HEIGHT_REDUCTION}px`;
      const docStyle = document.documentElement.style;
      if (docStyle.getPropertyValue(`--${REDESIGN_HEADER_HEIGHT_CSS_VARIABLE}`) === nextValue) {
        return;
      }
      docStyle.setProperty(`--${REDESIGN_HEADER_HEIGHT_CSS_VARIABLE}`, nextValue);
    };

    const { throttled: onResize, cancel: cancelPendingResize } =
      throttleToAnimationFrames(updateHeaderHeights);
    const resizeObserver = new ResizeObserver(onResize);
    resizeObserver.observe(topSectionElement);
    updateHeaderHeights();

    return () => {
      controlCookieContainerVisibility('block');
      document.documentElement.style.removeProperty('scroll-padding-top');
      document.documentElement.style.removeProperty(`--${REDESIGN_HEADER_BAR_HEIGHT_CSS_VARIABLE}`);
      resizeObserver.disconnect();
      cancelPendingResize();
    };
  }, []);

  useEffect(() => {
    if (!isMenuOpen) {
      controlCookieContainerVisibility('block');
    }
  }, [isMenuOpen]);

  const [prevIsMenuOpen, setPrevIsMenuOpen] = useState(isMenuOpen);
  if (isMenuOpen !== prevIsMenuOpen) {
    setPrevIsMenuOpen(isMenuOpen);
    if (!isMenuOpen) {
      setSelectedLevel1Index(null);
      setSelectedLevel2Index(null);
    }
  }

  const openMenu = () => {
    props.onMenuChanged?.();
    setMenuOpen(true);

    void amplitudeTrackEvent('Burger Menu Open', {
      custom_page: window.location.origin,
      custom_level: pageLevel,
      brand: amplitudeBrandName(buildType),
      buildType,
      language,
      ctaName: 'Burger Menu',
      component: 'Header Mobile',
    });

    controlCookieContainerVisibility('none');

    setSelectedLevel1Index(null);
    setSelectedLevel2Index(null);

    if (headerBarRef.current) {
      setHeaderBarHeightCssVariablePx(headerBarRef.current.offsetHeight);
    }
  };

  const goBack = () => {
    props.onMenuChanged?.();

    if (selectedLevel2Index !== null && selectedLevel2Index !== undefined) {
      setSelectedLevel2Index(null);
    } else {
      setSelectedLevel1Index(null);
    }
  };

  const closeMenu = () => {
    props.onMenuChanged?.();
    setMenuOpen?.(false);
  };

  const logoAmplitudeAction = () => {
    void amplitudeTrackEvent(amplitudeLogoClickEvent, {
      custom_page: window.location.origin,
      custom_level: pageLevel,
      brand: amplitudeBrandName(buildType),
      buildType,
      language,
      ctaName: 'Logo',
      component: 'Header',
    });
  };

  const logoClick = () => {
    closeMenu();
    logoAmplitudeAction();
    const prefersReducedMotion = matchReducedMotion().matches;
    document.body.scrollIntoView({ behavior: prefersReducedMotion ? 'instant' : 'smooth' });
  };

  const iconColor = isHarbour ? styling.colors.harbourGold : styling.colors.capeCod;

  return (
    <header
      className={classNames(styles.container, forceMobileHeader && styles.containerForceMobile)}
    >
      <div className={styles.header} ref={topSectionRef}>
        {ribbonChildren}
        <div className={styles.headerContentWrapper} ref={headerBarRef}>
          <div className={styles.headerContent}>
            <div className={styles.menuButtons}>
              {!isMenuOpen && (
                <button
                  type="button"
                  className={classNames(styles.menuButton, styles.openButton)}
                  onClick={openMenu}
                  aria-label="Open menu"
                >
                  <MenuIcon size="1.5rem" fill={iconColor} />
                </button>
              )}
              {isMenuOpen &&
                ((selectedLevel1Index !== null && selectedLevel1Index !== undefined) ||
                  isAdditionalSubMenuOpen) && (
                  <button type="button" className={styles.menuButton} onClick={goBack}>
                    <RedesignLeftChevronThin size="0.75rem" fill={iconColor} />
                    <div>{headerContent?.headerOtherText?.backText}</div>
                  </button>
                )}
            </div>
            <div className={styles.logo}>
              <WebsiteLink to="/" goToGlobal handleClick={logoClick}>
                <HeaderLogo
                  davidLloydSize="6.875rem"
                  meridianSize="7.25rem"
                  harbourSize="7.25rem"
                  togetherFoundationSize="7.25rem"
                />
              </WebsiteLink>
            </div>
            <div className={styles.buttonWrapper}>
              {primaryButton && !headerContent?.linkDropdownCta?.enabled && !isMenuOpen && (
                <HeaderButton {...primaryButton} contentClassName={styles.primaryButton} />
              )}
              {headerContent?.linkDropdownCta?.enabled && !isMenuOpen && (
                <RedesignLinkDropdownCta
                  links={headerContent.linkDropdownCta.dropdownOptions}
                  {...headerContent.linkDropdownCta}
                />
              )}
              {isMenuOpen && (
                <button type="button" className={styles.menuButton} onClick={closeMenu}>
                  <div>{headerContent?.headerOtherText?.closeText}</div>
                  <RedesignClose size="1rem" fill={iconColor} />
                </button>
              )}
            </div>
          </div>
          {!isGlobalPage && (
            <div className={styles.clubNameText}>
              {clubNameDependingOnBuildTypeAndLanguage(
                getClubInformation(siteId).clubName,
                buildType,
                language
              )}
            </div>
          )}
        </div>
      </div>
      {isMobileHeader && (
        <HeaderMenu
          headerContent={headerContent}
          isVisible={isMenuOpen}
          level1SelectedIndex={selectedLevel1Index}
          level2SelectedIndex={selectedLevel2Index}
          onSelectLevel1Index={setSelectedLevel1Index}
          onSelectLevel2Index={setSelectedLevel2Index}
          onFinish={closeMenu}
          primaryButton={primaryButton}
          secondaryButton={secondaryButton}
        >
          {additionalSubMenus}
        </HeaderMenu>
      )}
    </header>
  );
};

export default HeaderMobile;
